import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { apiUrl, UrlResolverService } from 'app/common/url-resolver.service';
import { Ajax } from 'app/common/ajax';
import { SimplePage } from 'app/common/types';
import { BUser } from './user';
import { isNil, omitBy } from 'lodash';
import { MessageHandlerService } from 'app/common/common/message-handler/message-handler.service';
import { downloadExcel } from 'app/common/util/dom';
import { BNotificationType } from '../notification-type/notification-type';

@Injectable()
export class UsersService {
  private readonly USERS_URL = apiUrl('users');
  private readonly USERS_REPORT_URL: string;
  private readonly USERS_APPROVAL_ASSIGNMENT_URL = apiUrl('users', 'enquiries-approval-assignment');

  constructor(
    private http: HttpClient,
    private urlResolverService: UrlResolverService,
    private messageService: MessageHandlerService
  ) {
    this.USERS_REPORT_URL = urlResolverService.apiUrlForPath(['reporting', 'users']);
  }

  deactivate(id: number, host?: string): Observable<'activated-legacy' | string> {
    return this.http.delete<'activated-legacy' | string>(this.resolveUrl(host, id));
  }

  bulkDeactivate(ids: number[], host?: string): Observable<void> {
    return this.http.delete<void>(`${this.resolveBaseUrl(host)}/`, { body: { ids } });
  }

  get(params: UsersService.UsersSearchParams, host?: string): Observable<UsersService.UsersResult> {
    const query = new Ajax.QueryParams()
      .addIfPresent('teamId', params.teamId)
      .addIfPresent('searchText', params.searchText)
      .addIfPresent('status', params.status)
      .addIfPresent('pageSize', params.pageSize)
      .addIfPresent('page', params.page)
      .addIfPresent('orderBy', params.orderBy)
      .toString();
    return this.http
      .get<UsersService.UsersResult>(this.resolveUrl(host, query), Ajax.X_NO_LOADING_OPTIONS)
      .pipe(
        map((res) => {
          res.records = BUser.fromRestArray(res.records);
          return res;
        })
      );
  }

  findUser(username: string, status: 'all' | 'active', host?: string): Observable<BUser> {
    username = username.toLowerCase();
    return this.get({ searchText: username, status: status }, host).pipe(
      map((res) => res.records.find((u) => u.username.toLowerCase() === username))
    );
  }

  create(data: UsersService.CreateUser, host?: string): Observable<number> {
    return this.http.post<number>(this.resolveUrl(host, ''), data);
  }

  updateDelegations(userId: number, data: UsersService.Delegations): Observable<string> {
    return this.http.put<string>(`${this.USERS_URL}/${userId}`, data);
  }

  getOutOfOfficeData(userId: number): Observable<UsersService.outOfOfficeData> {
    return this.http.get<UsersService.outOfOfficeData>(`${this.USERS_URL}/${userId}`).pipe(
      map((data) => {
        data.users = BUser.fromRestArray(data.users);
        data.delegators = BUser.fromRestArray(data.delegators);
        return data;
      })
    );
  }

  updateApprovalAssignmentData(
    userId: number,
    data: UsersService.PutApprovalsData
  ): Observable<void> {
    return this.http.put<void>(`${this.USERS_APPROVAL_ASSIGNMENT_URL}/${userId}`, data);
  }

  getApprovalAssignmentData(userId: number): Observable<UsersService.GetApprovalsData> {
    return this.http.get<UsersService.GetApprovalsData>(
      `${this.USERS_APPROVAL_ASSIGNMENT_URL}/${userId}`
    );
  }

  usersReportGenerateAndDownload(teamId: number | null = null): Observable<Blob> {
    return this.http
      .get<Blob>(this.USERS_REPORT_URL, {
        params: omitBy({ teamId }, isNil),
        responseType: 'blob' as 'json',
        ...Ajax.X_NO_LOADING_OPTIONS,
      })
      .pipe(
        tap((usersReport) => {
          downloadExcel(usersReport, 'Users-report');
        }),
        catchError(() => {
          this.messageService.error('An error occurred while fetching the users report.');
          return of(null);
        })
      );
  }

  getNotifications(userId: number): Observable<BNotificationType[]> {
    return this.http
      .get<BNotificationType[]>(apiUrl('users', 'notifications', userId))
      .pipe(map((resp) => BNotificationType.fromRestArray(resp)));
  }

  getUserRelatedInteractionsData(authRequestId: number): Observable<number[]> {
    const url = apiUrl('users', 'user-interactions-data-before-archiving', authRequestId);
    return this.http.get<number[]>(url);
  }

  private resolveUrl(host: string | undefined, sufix: string | number): string {
    return `${this.resolveBaseUrl(host)}/${sufix}`;
  }

  private resolveBaseUrl(host: string | undefined): string {
    return host ? this.urlResolverService.apiUrlForHost(host, 'users') : this.USERS_URL;
  }
}

export namespace UsersService {
  export type outOfOfficeData = {
    users: BUser[];
    delegators: BUser[];
  };

  export type UsersSearchParams = {
    teamId?: number;
    searchText?: string;
    status?: 'active' | 'inactive' | 'all';
    pageSize?: number;
    page?: number;
    orderBy?: string;
  };

  export type UsersResult = SimplePage<BUser>;

  export type Delegations = {
    autoAssignToId?: number;
    deputyId?: number;
    outOfOfficeImmediate?: boolean;
    outOfOfficeFrom?: Date | string;
    outOfOfficeTo?: Date | string;
  };

  export type PutApprovalsData = {
    selectedApprovalAssignToIds: number[];
    selectedApprovalAssignByIds: number[];
  };

  export type GetApprovalsData = {
    selectedApprovalAssignToIds: number[];
    selectedApprovalAssignByIds: number[];
    availableApprovalAssignToIds: number[];
    availableApprovalAssignByIds: number[];
    hasApproveEnqPerm: boolean;
  };

  export type CreateUser = {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    templateId?: number;
    affiliations?: { [teamId: number]: number[] };
    welcomeEmail?: boolean;
    syncRemote?: boolean;
    syncRemoteName?: string;
  };
}
